import { type OrderDetails } from '@koala/sdk';
import { useEffect, useRef, useState } from 'react';
import {
  StyledReorderList,
  StyledReorderHeading,
  StyledCollapseButton,
  StyledCollapsibleContainer,
  ReorderListPadding,
  StyledHistoryIconSvg,
  StyledReorderControls,
  StyledSliderNav,
} from './styles';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { ReorderListItem } from '@/components/reorders/listItem';
import { ViewAllReordersLink } from '@/components/reorders/viewAll';
import Icon from '@/components/uielements/icon';
import { Stack } from '@/components/uielements/stack/stack';
import { useDispatch, useSelector } from '@/redux';
import meActions from '@/redux/me/actions';
import { deriveOrdersByLocationID } from '@/utils/orders';

interface Props {
  locationID?: number;
  ordersToDisplay?: number;
  collapsible?: boolean;
  showIcon?: boolean;
  visibleDesktopCards?: number;
}

export const ReorderList = ({
  locationID,
  ordersToDisplay = 3,
  collapsible = true,
  showIcon = true,
  visibleDesktopCards,
}: Props) => {
  const pastOrders = useSelector((state) => state.app.me.pastOrders);
  const orderCompleted = useSelector((state) => state.app.basket.orderCompleted);
  const dispatch = useDispatch();
  const [listCollapse, setListCollapse] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const reorderListRef = useRef(null);
  const scrollRef = useRef<HTMLUListElement>(null);
  const scrollTargetRef = useRef<number | null>(null);

  const reorders = deriveOrdersByLocationID(pastOrders, locationID).slice(0, ordersToDisplay);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const onDown = (e: MouseEvent) => {
      isDown = true;
      startX = e.pageX;
      scrollLeft = el.scrollLeft;
    };
    const onMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      el.scrollLeft = scrollLeft - (e.pageX - startX);
    };
    const onUp = () => {
      isDown = false;
    };
    const onDragStart = (e: Event) => {
      e.preventDefault();
    };

    el.addEventListener('mousedown', onDown);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseup', onUp);
    el.addEventListener('mouseleave', onUp);
    el.addEventListener('dragstart', onDragStart);

    return () => {
      el.removeEventListener('mousedown', onDown);
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseup', onUp);
      el.removeEventListener('mouseleave', onUp);
      el.removeEventListener('dragstart', onDragStart);
    };
  }, [reorders.length]);

  // Track scroll position so the prev/next arrows can reflect the Figma states:
  // hide the controls entirely when the row doesn't overflow, and disable an
  // arrow once the row is scrolled to the first (back) or last (forward) card.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateNavState = () => {
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      setIsOverflowing(maxScrollLeft > 1);
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft >= maxScrollLeft - 1);
    };

    updateNavState();
    el.addEventListener('scroll', updateNavState, { passive: true });

    // Prefer ResizeObserver over the window `resize` event: it also catches
    // container-driven resizes that leave the viewport unchanged (e.g. the Map
    // Page sidebar/panel changing width). We observe the first card in addition
    // to the row, because `scrollWidth` is a content measurement — if a card
    // ever grows from its own content (a longer store label, wider webfont
    // glyphs) the row's border box may not change, so observing only the
    // container would miss the new overflow. `resize` is kept solely as the
    // fallback for environments without ResizeObserver.
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateNavState);
      observer.observe(el);
      if (el.children[1]) observer.observe(el.children[1]);
    } else {
      window.addEventListener('resize', updateNavState);
    }

    return () => {
      el.removeEventListener('scroll', updateNavState);
      window.removeEventListener('resize', updateNavState);
      observer?.disconnect();
    };
  }, [reorders.length]);

  // Arrow paging accumulates into scrollTargetRef (see scrollByCard) so rapid
  // clicks advance the full N cards instead of under-advancing. Release the
  // target once the row lands on it, or settles after a drag/swipe, so manual
  // scrolling becomes the baseline for the next click.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const releaseIfArrived = () => {
      const target = scrollTargetRef.current;
      if (target !== null && Math.abs(el.scrollLeft - target) <= 1) {
        scrollTargetRef.current = null;
      }
    };
    const release = () => {
      scrollTargetRef.current = null;
    };

    el.addEventListener('scroll', releaseIfArrived, { passive: true });
    el.addEventListener('scrollend', release);
    return () => {
      el.removeEventListener('scroll', releaseIfArrived);
      el.removeEventListener('scrollend', release);
    };
  }, [reorders.length]);

  useEffect(() => {
    if (orderCompleted) {
      dispatch(meActions.fetchMePastOrders());
    }
  }, [orderCompleted]);

  /**
   * Page the recent-orders row by one card, preserving the native scroll/drag
   * interaction. We measure the live card width (+ row gap) so the step is
   * correct across breakpoints and both widget mounts; scroll position is then
   * clamped by the browser, which keeps the edge-disabled states honest.
   *
   * @param direction -1 to page back (left), 1 to page forward (right)
   */
  const scrollByCard = (direction: 1 | -1): void => {
    const el = scrollRef.current;
    if (!el) return;

    // children[0] is the leading padding spacer; the first real card is [1].
    const firstCard = el.children[1] as HTMLElement | undefined;
    const styles = window.getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 0;
    const step = cardWidth > 0 ? cardWidth + gap : el.clientWidth * 0.8;

    // Page from the last requested target, not the live scrollLeft: scrollTo
    // relative to an in-flight smooth scroll under-advances, so three quick
    // Next clicks would move less than three cards. Accumulate onto the pending
    // target, clamp to the scrollable range, and let the scrollTargetRef effect
    // release it on settle so drag/swipe stays authoritative.
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const from = scrollTargetRef.current ?? el.scrollLeft;
    const next = Math.max(0, Math.min(from + direction * step, maxScrollLeft));
    scrollTargetRef.current = next;

    el.scrollTo({ left: next, behavior: 'smooth' });
  };

  /**
   * Hydrate the reorder modal details and toggle the reorder modal open
   *
   * @param orderDetails OrderDetails
   */
  const hydrateAndToggleReorderModal = (orderDetails: OrderDetails): void => {
    dispatch(meActions.fetchMeOrderDetailsSuccess(orderDetails));
    dispatch(meActions.toggleReorderReceipt(true));
  };

  /**
   * Handle the collapse chevron button click
   */
  const handleCollapseClick = (): void => {
    setListCollapse(!listCollapse);
  };

  // Triple check we have reorders to display before showing the reorder interface
  // (This is applicable in scenarios where a user has past orders but none for a specific store)
  if (!reorders.length) {
    return null;
  }

  const ariaControlsID = 'recent-orders';

  return (
    <div ref={reorderListRef} className="koala__ui-reorders-container-div">
      <Stack overrides={{ margin: 'var(--size-4) 0' }}>
        {showIcon && <StyledHistoryIconSvg title="History icon" />}
        <StringAccessor tag={StyledReorderHeading} accessor="reorders.recent_orders_title" />
        <ViewAllReordersLink expand={collapsible} />
        {collapsible && (
          <StyledCollapseButton
            onClick={() => handleCollapseClick()}
            invert={listCollapse}
            aria-expanded={!listCollapse}
            aria-controls={ariaControlsID}
            id={`controls-${ariaControlsID}`}
          />
        )}
      </Stack>

      {isOverflowing && !listCollapse && (
        <StyledReorderControls role="group" aria-label="Recent orders navigation">
          <StyledSliderNav
            type="button"
            $back
            onClick={() => !atStart && scrollByCard(-1)}
            aria-disabled={atStart}
            aria-controls={ariaControlsID}
            aria-label="Previous orders"
          >
            <Icon.Caret title="Previous orders" aria-hidden="true" />
          </StyledSliderNav>
          <StyledSliderNav
            type="button"
            onClick={() => !atEnd && scrollByCard(1)}
            aria-disabled={atEnd}
            aria-controls={ariaControlsID}
            aria-label="Next orders"
          >
            <Icon.Caret title="Next orders" aria-hidden="true" />
          </StyledSliderNav>
        </StyledReorderControls>
      )}

      <StyledCollapsibleContainer
        collapse={listCollapse}
        role="region"
        tabIndex={-1}
        id={ariaControlsID}
      >
        <StyledReorderList ref={scrollRef} style={{ cursor: 'grab' }}>
          <ReorderListPadding />
          {reorders.map((order, i) => (
            <ReorderListItem
              orderDetails={order}
              key={`${order.id}-${i}`}
              showReorderReceipt={hydrateAndToggleReorderModal}
              visibleDesktopCards={visibleDesktopCards}
            />
          ))}
          <ReorderListPadding />
        </StyledReorderList>
      </StyledCollapsibleContainer>
    </div>
  );
};
